import { supabase } from '../config/supabase';
import { progressStageService } from './progressStageService';
import { progressStageSettingsService } from './progressStageSettingsService';
import { getDefaultAutomationRules, ChecklistAutomationRule as DefaultRule } from '../data/checklistAutomationData';
import { onboardingStepsService } from './onboardingStepsService';

export interface ChecklistAutomationRule {
  id: string;
  org_id: number;
  check_list_num: string;
  checklist_label?: string;
  progress: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Service for managing checklist automation settings
 * Maps checklist numbers (checkListNum) to progress stages (0-6)
 */
export const checklistAutomationService = {
  /**
   * Get checklist label from onboarding steps based on checklist number
   */
  async getChecklistLabel(orgId: number, checkListNum: string): Promise<string | null> {
    try {
      // Get onboarding steps for the organization
      const steps = await onboardingStepsService.getOnboardingSteps(orgId);
      
      // Convert checklist number to index (1-based to 0-based)
      const index = parseInt(checkListNum, 10) - 1;
      
      if (index >= 0 && index < steps.length) {
        return steps[index].title;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting checklist label:', error);
      return null;
    }
  },

  /**
   * Get all automation rules for an organization
   * Returns custom rules from database if they exist, otherwise returns default rules
   * Automatically populates checklist_label if missing
   */
  async getAutomationRules(orgId: number): Promise<ChecklistAutomationRule[]> {
    try {
      // Try to fetch custom rules from database
      const { data, error } = await supabase
        .from('checklist_automation_settings')
        .select('*')
        .eq('org_id', orgId)
        .order('check_list_num', { ascending: true });

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is fine - use defaults
        console.error('Error fetching automation rules:', error);
        // Fall back to default rules
        return await this.getDefaultRulesAsDatabaseFormat(orgId);
      }

      // If we have custom rules, populate missing checklist_labels and return them
      if (data && data.length > 0) {
        // Populate missing checklist_labels
        const rulesWithLabels = await Promise.all(
          data.map(async (rule) => {
            if (!rule.checklist_label) {
              const label = await this.getChecklistLabel(orgId, rule.check_list_num);
              if (label) {
                // Update the rule in database with the label
                await this.updateChecklistLabel(orgId, rule.check_list_num, label);
                return { ...rule, checklist_label: label };
              }
            }
            return rule;
          })
        );
        // Sort by checklist number numerically (not alphabetically)
        return rulesWithLabels.sort((a, b) => {
          const numA = parseInt(a.check_list_num, 10);
          const numB = parseInt(b.check_list_num, 10);
          return numA - numB;
        });
      }

      return await this.getDefaultRulesAsDatabaseFormat(orgId);
    } catch (error) {
      console.error('Error in getAutomationRules:', error);
      // Fall back to default rules on any error
      return await this.getDefaultRulesAsDatabaseFormat(orgId);
    }
  },

  /**
   * Update checklist_label for a rule
   */
  async updateChecklistLabel(orgId: number, checkListNum: string, label: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('checklist_automation_settings')
        .update({ checklist_label: label })
        .eq('org_id', orgId)
        .eq('check_list_num', checkListNum);

      if (error) {
        console.error('Error updating checklist label:', error);
        // Don't throw, just log - this is a non-critical update
      }
    } catch (error) {
      console.error('Error in updateChecklistLabel:', error);
      // Don't throw - this is a non-critical update
    }
  },

  /**
   * Get default rules in database format (for display purposes)
   * Populates checklist_label from onboarding steps
   */
  async getDefaultRulesAsDatabaseFormat(orgId: number): Promise<ChecklistAutomationRule[]> {
    const defaultRules = getDefaultAutomationRules();
    // Convert to database format (with id, timestamps, etc. for UI consistency)
    // Populate checklist_label from onboarding steps
    const rulesWithLabels = await Promise.all(
      defaultRules.map(async (rule, index) => {
        const label = await this.getChecklistLabel(orgId, rule.check_list_num);
        return {
          id: `default-${index}`,
          org_id: orgId,
          check_list_num: rule.check_list_num,
          checklist_label: label || undefined,
          progress: rule.progress,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: undefined,
        };
      })
    );
    // Sort by checklist number numerically (not alphabetically)
    return rulesWithLabels.sort((a, b) => {
      const numA = parseInt(a.check_list_num, 10);
      const numB = parseInt(b.check_list_num, 10);
      return numA - numB;
    });
  },

  /**
   * Get the progress stage for a specific checklist number
   * Returns default if no custom rule exists in database
   */
  async getProgressForChecklistNum(orgId: number, checkListNum: string): Promise<number | null> {
    try {
      // Try to fetch custom rule from database
      const { data, error } = await supabase
        .from('checklist_automation_settings')
        .select('progress')
        .eq('org_id', orgId)
        .eq('check_list_num', checkListNum)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No row found - check defaults
          const defaultRules = getDefaultAutomationRules();
          const defaultRule = defaultRules.find(r => r.check_list_num === checkListNum);
          return defaultRule ? defaultRule.progress : null;
        }
        console.error('Error fetching progress for checklist num:', error);
        // Fall back to defaults
        const defaultRules = getDefaultAutomationRules();
        const defaultRule = defaultRules.find(r => r.check_list_num === checkListNum);
        return defaultRule ? defaultRule.progress : null;
      }

      return data?.progress ?? null;
    } catch (error) {
      console.error('Error in getProgressForChecklistNum:', error);
      // Fall back to defaults
      const defaultRules = getDefaultAutomationRules();
      const defaultRule = defaultRules.find(r => r.check_list_num === checkListNum);
      return defaultRule ? defaultRule.progress : null;
    }
  },

  /**
   * Save or update an automation rule
   * Automatically populates checklist_label if not provided
   */
  async saveAutomationRule(
    orgId: number,
    checkListNum: string,
    progress: number,
    userId?: string,
    checklistLabel?: string
  ): Promise<void> {
    try {
      // Get actual progress stages for the organization to validate against
      const stages = await progressStageSettingsService.getStages(orgId);
      const validProgressValues = stages.map(s => s.value);
      
      // If no stages exist, fall back to default validation (0-6)
      if (validProgressValues.length === 0) {
        if (progress < 0 || progress > 6) {
          throw new Error('Progress must be between 0 and 6');
        }
      } else {
        // Validate progress matches one of the saved progress stages
        const minProgress = Math.min(...validProgressValues);
        const maxProgress = Math.max(...validProgressValues);
        
        if (!validProgressValues.includes(progress)) {
          throw new Error(`Progress must be between ${minProgress} and ${maxProgress} (must match a saved progress stage)`);
        }
      }

      // Get checklist label if not provided
      let label = checklistLabel;
      if (!label) {
        const fetchedLabel = await this.getChecklistLabel(orgId, checkListNum);
        label = fetchedLabel || undefined;
      }

      const { error } = await supabase
        .from('checklist_automation_settings')
        .upsert({
          org_id: orgId,
          check_list_num: checkListNum,
          checklist_label: label,
          progress: progress,
          updated_at: new Date().toISOString(),
          created_by: userId || null,
        }, {
          onConflict: 'org_id,check_list_num'
        });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error saving automation rule:', error);
      throw error;
    }
  },

  /**
   * Delete an automation rule
   */
  async deleteAutomationRule(orgId: number, checkListNum: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('checklist_automation_settings')
        .delete()
        .eq('org_id', orgId)
        .eq('check_list_num', checkListNum);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting automation rule:', error);
      throw error;
    }
  },

  /**
   * Get the reverse mapping: progress stage to checklist number
   * Returns the first checklist number that maps to the given progress
   * Falls back to defaults if no custom rule exists
   */
  async getCheckListNumForProgress(orgId: number, progress: number): Promise<string | null> {
    try {
      // Try to fetch custom rule from database
      const { data, error } = await supabase
        .from('checklist_automation_settings')
        .select('check_list_num')
        .eq('org_id', orgId)
        .eq('progress', progress)
        .order('check_list_num', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No row found - check defaults
          const defaultRules = getDefaultAutomationRules();
          const defaultRule = defaultRules.find(r => r.progress === progress);
          return defaultRule ? defaultRule.check_list_num : null;
        }
        console.error('Error fetching checklist num for progress:', error);
        // Fall back to defaults
        const defaultRules = getDefaultAutomationRules();
        const defaultRule = defaultRules.find(r => r.progress === progress);
        return defaultRule ? defaultRule.check_list_num : null;
      }

      return data?.check_list_num ?? null;
    } catch (error) {
      console.error('Error in getCheckListNumForProgress:', error);
      // Fall back to defaults
      const defaultRules = getDefaultAutomationRules();
      const defaultRule = defaultRules.find(r => r.progress === progress);
      return defaultRule ? defaultRule.check_list_num : null;
    }
  },

  /**
   * Seed default automation rules based on the hardcoded logic in OnboardingSteps.tsx
   * This should be run once per organization to initialize the automation rules
   * Automatically populates checklist_label from onboarding steps
   */
  async seedDefaultRules(orgId: number, userId?: string): Promise<{ success: boolean; message: string }> {
    try {
      const defaultRules = [
        { check_list_num: '1', progress: 0 }, // Invitation Sent / Pending Enrollment
        { check_list_num: '2', progress: 2 }, // Enrolled in Course
        { check_list_num: '3', progress: 3 }, // Passed Test Waiting License Approval
        { check_list_num: '4', progress: 4 }, // Company Onboarding Waiting Agent Number
        { check_list_num: '9', progress: 5 }, // Company Onboarding Agent Number Approved
        { check_list_num: '14', progress: 6 }, // Agent Number Approved Waiting for Training
      ];

      const results: string[] = [];

      for (const rule of defaultRules) {
        try {
          // Get checklist label from onboarding steps
          const label = await this.getChecklistLabel(orgId, rule.check_list_num);
          await this.saveAutomationRule(orgId, rule.check_list_num, rule.progress, userId, label || undefined);
          results.push(`✓ Seeded rule: checklist ${rule.check_list_num} → progress ${rule.progress}${label ? ` (${label})` : ''}`);
        } catch (error: any) {
          results.push(`⊘ Failed to seed rule for checklist ${rule.check_list_num}: ${error.message}`);
        }
      }

      return {
        success: true,
        message: `Seeding complete:\n${results.join('\n')}`
      };
    } catch (error: any) {
      console.error('Error seeding default rules:', error);
      return {
        success: false,
        message: `Error seeding rules: ${error.message}`
      };
    }
  },
};

